import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import useFetchCandidateProfilesCustomFields from '@/features/agent/candidates/api/useFetchCandidateProfilesCustomFields';
import { MdArrowForward, MdArrowBack } from 'react-icons/md';
import Loading from '@/components/Loading';

interface Props {
  onNext: () => void;
  onBack: () => void;
  selectedFieldIds: Set<number>;
  onSelectedFieldIdsChange: (fieldIds: Set<number>) => void;
}

export default function FormSetupTutorialStep2({
  onNext,
  onBack,
  selectedFieldIds,
  onSelectedFieldIdsChange,
}: Props) {
  const { data: customFieldsData, isLoading } = useFetchCandidateProfilesCustomFields();

  const customFields = customFieldsData?.records || [];

  const handleFieldToggle = (fieldId: number) => {
    const newSet = new Set(selectedFieldIds);
    if (newSet.has(fieldId)) {
      newSet.delete(fieldId);
    } else {
      newSet.add(fieldId);
    }
    onSelectedFieldIdsChange(newSet);
  };

  if (isLoading) {
    return (
      <div className="relative h-[684px] overflow-hidden rounded-b-md bg-white">
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[684px] flex-col overflow-hidden rounded-b-md bg-white">
      {/* メインコンテンツ部分 */}
      <div
        className="flex flex-1 flex-col items-center overflow-hidden px-x4l py-xl"
        style={{
          backgroundImage:
            'linear-gradient(119.14346197917388deg, var(--semantic/primary_lighten, rgba(237, 248, 255, 1)) 0%, var(--semantic/background/white, rgba(255, 255, 255, 1)) 30%, var(--semantic/background/white, rgba(255, 255, 255, 1)) 80%, var(--primitive/purple/50, rgba(246, 243, 255, 1)) 100%)',
        }}
      >
        <div className="flex w-full flex-col items-center gap-xl">
          {/* タイトル部分 */}
          <div className="flex flex-col items-center gap-xs">
            <h2 className="text-center text-base leading-normal font-bold text-main">
              エントリーフォームでの質問項目を以下から全て選択しましょう！
            </h2>
            <p className="text-center text-xs leading-normal font-bold text-sub">
              （設定した内容は、後からいつでも編集することができます。）
            </p>
          </div>

          {/* カスタムフィールド一覧 */}
          <div className="flex w-full flex-col items-center shadow-[0px_0px_2px_0px_rgba(0,0,0,0.2)]">
            <div className="w-full rounded-md bg-white px-x4l py-lg">
              <div className="flex flex-wrap gap-6">
                {/* 氏名 */}
                <div className="w-[calc((100%-72px)/4)]">
                  <Checkbox checked disabled>
                    <span className="text-sm leading-[1.7] font-normal text-main">氏名</span>
                  </Checkbox>
                </div>

                {/* カスタムフィールド */}
                {customFields.map((field) => (
                  <div key={field.id} className="w-[calc((100%-72px)/4)]">
                    <Checkbox
                      checked={selectedFieldIds.has(field.id)}
                      onChange={() => handleFieldToggle(field.id)}
                    >
                      <span className="text-sm leading-[1.7] font-normal text-main">
                        {field.name}
                      </span>
                    </Checkbox>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* フッター部分 */}
      <div className="flex items-center justify-center gap-md rounded-b-md border-t border-solid border-border bg-white px-lg py-md">
        <Button color="secondary" size="md" onClick={onBack} className="gap-xxs">
          <MdArrowBack className="size-4" />
          <span>戻る</span>
        </Button>
        <Button color="primary" size="md" onClick={onNext} className="gap-xxs">
          <span>次へ</span>
          <MdArrowForward className="size-4" />
        </Button>
      </div>
    </div>
  );
}
